// src/pages/NotificationPreferences.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageSquare, Bell, Smartphone, Calendar, DollarSign, BookOpen, Users, Settings, Check, Info } from 'lucide-react';

const NotificationPreferences = () => {
  const navigate = useNavigate();
  const [saveStatus, setSaveStatus] = useState('');
  const [emailFrequency, setEmailFrequency] = useState('instant');
  const [phoneNumber, setPhoneNumber] = useState('+1 (555) 123-4567');
  const [smsEnabled, setSmsEnabled] = useState(false);
  
  // Get user role for role-specific notifications
  const userRole = localStorage.getItem('userRole') || 'practitioner';
  
  // Notification preferences state
  const [preferences, setPreferences] = useState({
    // Account & Security
    accountSecurity: {
      email: true,
      push: true,
      sms: false,
      label: 'Account & Security',
      description: 'Password changes, login attempts, security alerts',
      icon: Settings,
      priority: true
    },
    
    // Transactional
    bookingConfirmations: {
      email: true,
      push: true,
      sms: true,
      label: 'Booking Confirmations',
      description: 'Session bookings, cancellations, and changes',
      icon: Calendar,
      priority: true
    },
    
    paymentReceipts: {
      email: true,
      push: false,
      sms: false,
      label: 'Payment & Receipts',
      description: 'Payment confirmations, invoices, refunds',
      icon: DollarSign,
      priority: true
    },
    
    // Engagement
    sessionReminders: {
      email: true,
      push: true,
      sms: true,
      label: 'Session Reminders',
      description: '24-hour and 1-hour reminders before sessions',
      icon: Bell
    },
    
    newMessages: {
      email: true,
      push: true,
      sms: false,
      label: 'Messages',
      description: 'Direct messages from practitioners or clients',
      icon: MessageSquare
    },
    
    courseUpdates: {
      email: true,
      push: true,
      sms: false,
      label: 'Course Updates',
      description: 'New lessons, course completions, certificates',
      icon: BookOpen
    },
    
    // Marketing
    weeklyNewsletter: {
      email: true,
      push: false,
      sms: false,
      label: 'Weekly Newsletter',
      description: 'Tips, new features, and platform updates',
      icon: Mail
    },
    
    newPractitioners: {
      email: false,
      push: false,
      sms: false,
      label: 'New Practitioners',
      description: 'Notifications about new practitioners in your area',
      icon: Users
    },
    
    promotions: {
      email: false,
      push: false,
      sms: false,
      label: 'Promotions & Offers',
      description: 'Special offers, discounts, and promotional events',
      icon: DollarSign
    }
  });
  
  // Practitioner-specific notifications
  const practitionerNotifications = {
    newBookings: {
      email: true,
      push: true,
      sms: true,
      label: 'New Bookings',
      description: 'Instant alerts when clients book sessions',
      icon: Calendar,
      priority: true
    },
    
    clientReviews: {
      email: true,
      push: true,
      sms: false,
      label: 'Client Reviews',
      description: 'Notifications when clients leave reviews',
      icon: Users
    },
    
    earningsReports: {
      email: true,
      push: false,
      sms: false,
      label: 'Earnings Reports',
      description: 'Weekly and monthly earnings summaries',
      icon: DollarSign
    }
  };
  
  // Add practitioner notifications if user is a practitioner
  const allPreferences = userRole === 'practitioner' 
    ? { ...preferences, ...practitionerNotifications }
    : preferences;
  
  // Handle toggle change
  const handleToggle = (category, channel) => {
    const newPreferences = {
      ...allPreferences,
      [category]: {
        ...allPreferences[category],
        [channel]: !allPreferences[category][channel]
      }
    };
    
    setPreferences(newPreferences);
    
    // Show save status
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
    }, 500);
  };
  
  // Quick presets
  const applyPreset = (preset) => {
    let newPreferences = { ...allPreferences };
    
    switch(preset) {
      case 'all':
        Object.keys(newPreferences).forEach(key => {
          newPreferences[key] = {
            ...newPreferences[key],
            email: true,
            push: true,
            sms: false
          };
        });
        break;
      case 'essential':
        Object.keys(newPreferences).forEach(key => {
          const isPriority = newPreferences[key].priority;
          newPreferences[key] = {
            ...newPreferences[key],
            email: isPriority,
            push: isPriority,
            sms: false
          };
        });
        break;
      case 'none':
        Object.keys(newPreferences).forEach(key => {
          const isPriority = newPreferences[key].priority;
          newPreferences[key] = {
            ...newPreferences[key],
            email: isPriority, // Keep essential emails
            push: false,
            sms: false
          };
        });
        break;
      default:
        break;
    }
    
    setPreferences(newPreferences);
    setSaveStatus('Preset applied');
    setTimeout(() => setSaveStatus(''), 2000);
  };
  
  // Group notifications by category
  const essentialNotifications = Object.entries(allPreferences).filter(([_, pref]) => pref.priority);
  const engagementNotifications = Object.entries(allPreferences).filter(([key, pref]) => 
    !pref.priority && ['sessionReminders', 'newMessages', 'courseUpdates', 'clientReviews'].includes(key)
  );
  const marketingNotifications = Object.entries(allPreferences).filter(([key, pref]) => 
    !pref.priority && ['weeklyNewsletter', 'newPractitioners', 'promotions', 'earningsReports'].includes(key)
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard/account')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Notification Preferences</h1>
                <p className="text-sm text-slate-600 mt-1">Choose how you want to be notified</p>
              </div>
            </div>
            
            {/* Save Status */}
            {saveStatus && (
              <div className="flex items-center gap-2 text-sm">
                {saveStatus === 'saving' ? (
                  <div className="flex items-center gap-2 text-slate-600">
                    <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="w-4 h-4" />
                    <span>{saveStatus}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Quick Settings Card */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  onClick={() => applyPreset('all')}
                  className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-center"
                >
                  <Bell className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="font-medium text-slate-900">All Notifications</p>
                  <p className="text-xs text-slate-600 mt-1">Get all updates</p>
                </button>
                
                <button
                  onClick={() => applyPreset('essential')}
                  className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-center"
                >
                  <Settings className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <p className="font-medium text-slate-900">Essential Only</p>
                  <p className="text-xs text-slate-600 mt-1">Important updates</p>
                </button>
                
                <button
                  onClick={() => applyPreset('none')}
                  className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-center"
                >
                  <Bell className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                  <p className="font-medium text-slate-900">Minimal</p>
                  <p className="text-xs text-slate-600 mt-1">Critical only</p>
                </button>
              </div>
            </div>

            {/* Essential Notifications */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Essential Notifications</h2>
                <p className="text-sm text-slate-600 mt-1">These keep your account secure and informed</p>
              </div>
              
              <div className="space-y-4">
                {essentialNotifications.map(([key, pref]) => {
                  const Icon = pref.icon;
                  return (
                    <div key={key} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Icon className="w-5 h-5 text-slate-600" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-medium text-slate-900">{pref.label}</h3>
                              <p className="text-sm text-slate-600 mt-1">{pref.description}</p>
                              {pref.priority && (
                                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                  <Info className="w-3 h-3" />
                                  Required for account security
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-6 mt-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pref.email}
                                onChange={() => handleToggle(key, 'email')}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">Email</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pref.push}
                                onChange={() => handleToggle(key, 'push')}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">Push</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pref.sms}
                                onChange={() => handleToggle(key, 'sms')}
                                disabled={!smsEnabled}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 disabled:opacity-50"
                              />
                              <span className="text-sm text-slate-700">SMS</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Engagement Notifications */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Engagement & Updates</h2>
                <p className="text-sm text-slate-600 mt-1">Stay connected with your activities</p>
              </div>
              
              <div className="space-y-4">
                {engagementNotifications.map(([key, pref]) => {
                  const Icon = pref.icon;
                  return (
                    <div key={key} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Icon className="w-5 h-5 text-slate-600" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-medium text-slate-900">{pref.label}</h3>
                              <p className="text-sm text-slate-600 mt-1">{pref.description}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-6 mt-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pref.email}
                                onChange={() => handleToggle(key, 'email')}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">Email</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pref.push}
                                onChange={() => handleToggle(key, 'push')}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">Push</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pref.sms}
                                onChange={() => handleToggle(key, 'sms')}
                                disabled={!smsEnabled}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 disabled:opacity-50"
                              />
                              <span className="text-sm text-slate-700">SMS</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Marketing Notifications */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Marketing & Promotions</h2>
                <p className="text-sm text-slate-600 mt-1">News, offers, and platform updates</p>
              </div>
              
              <div className="space-y-4">
                {marketingNotifications.map(([key, pref]) => {
                  const Icon = pref.icon;
                  return (
                    <div key={key} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Icon className="w-5 h-5 text-slate-600" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-medium text-slate-900">{pref.label}</h3>
                              <p className="text-sm text-slate-600 mt-1">{pref.description}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-6 mt-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pref.email}
                                onChange={() => handleToggle(key, 'email')}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">Email</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pref.push}
                                onChange={() => handleToggle(key, 'push')}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">Push</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pref.sms}
                                onChange={() => handleToggle(key, 'sms')}
                                disabled={!smsEnabled}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 disabled:opacity-50"
                              />
                              <span className="text-sm text-slate-700">SMS</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Email Frequency */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Email Frequency</h3>
              
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    value="instant"
                    checked={emailFrequency === 'instant'}
                    onChange={(e) => setEmailFrequency(e.target.value)}
                    className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-medium text-slate-900">Instant</p>
                    <p className="text-xs text-slate-600">Get emails as they happen</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    value="daily"
                    checked={emailFrequency === 'daily'}
                    onChange={(e) => setEmailFrequency(e.target.value)}
                    className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-medium text-slate-900">Daily Digest</p>
                    <p className="text-xs text-slate-600">One summary email per day</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    value="weekly"
                    checked={emailFrequency === 'weekly'}
                    onChange={(e) => setEmailFrequency(e.target.value)}
                    className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-medium text-slate-900">Weekly Summary</p>
                    <p className="text-xs text-slate-600">Weekly roundup on Mondays</p>
                  </div>
                </label>
              </div>
            </div>

            {/* SMS Settings */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Smartphone className="w-5 h-5 text-slate-600" />
                <h3 className="font-semibold text-slate-900">SMS Settings</h3>
              </div>
              
              {!smsEnabled ? (
                <div>
                  <p className="text-sm text-slate-600 mb-4">
                    Enable SMS notifications for important updates
                  </p>
                  <button
                    onClick={() => setSmsEnabled(true)}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                  >
                    Enable SMS
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-xs text-slate-600">
                      Standard messaging rates may apply
                    </p>
                    <button
                      onClick={() => setSmsEnabled(false)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Disable SMS
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Notification Summary */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Your Settings</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Email notifications</span>
                  <span className="font-medium text-slate-900">
                    {Object.values(allPreferences).filter(p => p.email).length} active
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Push notifications</span>
                  <span className="font-medium text-slate-900">
                    {Object.values(allPreferences).filter(p => p.push).length} active
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">SMS notifications</span>
                  <span className="font-medium text-slate-900">
                    {smsEnabled ? `${Object.values(allPreferences).filter(p => p.sms).length} active` : 'Disabled'}
                  </span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-slate-200">
                <button className="w-full text-center text-blue-600 hover:text-blue-700 font-medium text-sm">
                  Unsubscribe from all
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;